// @flow

import $ from "vendors/zepto";
import * as React from "react";

import { remove } from "lodash";
import { BindAll, Throttle } from "lodash-decorators";
import OrderItem from "models/order_item";
import Cart from "models/cart";

import OrderItemDescriptor from "components/order_item_descriptor";
import SentryBoundary from "components/error_boundary";
import { CountrySelector, to_country_name } from "components/country_selector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "utils/fontawesome";
import { redirect } from "utils/common";
import UpsellingItemDescriptor from "components/upselling_item_descriptor";
import { random_str } from "utils/str_utils";

type Props = {
  cart: Cart,
  hasCheckout: boolean
};

@BindAll()
export default class Main extends React.Component<Props> {
  handleDelete(order_item: OrderItem): void {
    this.props.cart.remove_order_item(order_item, () => {
      // jQuery event handler updates the top right cart badge. The following
      // code will trigger a redraw in `components/cart.js`.
      $(".navbar.navbar-top .cart a").trigger(
        "updated",
        this.props.cart.order_items.length
      );
      this.forceUpdate();
    });
  }

  handleQuantityChanged(): void {
    this.props.cart.populate_with_server_data(() => {
      this.forceUpdate();
    });
  }

  handlePostalCodeChanged(e: SyntheticKeyboardEvent<> & SyntheticInputEvent<>) {
    // since we don't want to set the postal code until
    // the customer presses Enter, here we are making the postal code
    // a "uncontrolled" component deliberately.
    if (e.keyCode === 13) {
      this.props.cart.update_postal_details(
        // $FlowIgnore
        { postal_code: e.target.value },
        () => {
          this.forceUpdate();
        }
      );
    }
  }

  handleCountryChanged = (country_code: string): void => {
    this.props.cart.update_postal_details(
      { country_code: country_code },
      () => {
        this.forceUpdate();
      }
    );
  };

  handleCheckout = (): void => {
    if (window.isAuthenticatedUser) {
      return redirect(`/checkout/${this.props.cart.id}?ts=${Date.now()}`);
    }

    // If the current visitor has not logged in yet, pop up the authentication modal
    // for the customer to login or sign up. Upon a successful sign in,
    // the modal will reload the page,  which then triggers the `cart/index.jsx` to
    // assign the cart to the customer. Please refer to `cart/index.jsx:init()`.
    import(
      /* webpackChunkName: "components_authentication_modal" */ "components/authentication-modal/launch"
    ).then(({ default: launch }) => {
      localStorage.setItem("redirect_to_checkout", "true");
      launch();
    });
  };

  addUpsellingItemToCart = (orderItem: OrderItem) => {
    // Add an upselling item to the shopping cart.
    //
    // There are two steps involved in doing this:
    // 1. Create and save the orderItem to server, so it acquires a legit
    //    value for its `id`.
    // 2. Add it to the current cart on the server side through a `PUT`.
    //
    // This double round trip to server slows down visual feedback loop.
    // The customer will click "Add to cart" and then wait 2 seconds for
    // the change to take effect.
    //
    // The following code tries to trigger `enforceUpdate` twice.
    // The first time supplies a temporary id to the order item and add it
    // to the cart, so the user will see the change right away.
    //
    // The last step creates a temporary inconsistency between the in-memory
    // objects and our backend data, which we reconcile in the next step.
    // After the user sees the changes, we will save
    // the order item to server, add it to the cart and trigger a new refresh
    // so the HTML is consistent with the server side data again.
    const cart = this.props.cart;
    orderItem.id = random_str();
    cart.order_items.push(orderItem);
    // eslint-disable-next-line lodash/prefer-immutable-method
    remove(
      cart.recommendations,
      // eslint-disable-next-line lodash/matches-shorthand
      option =>
        option.product === orderItem.product && option.size === orderItem.size
    );
    this.forceUpdate();

    orderItem.id = "";
    orderItem.save({
      success: () => {
        // The orderItem.save's callback will set the id.
        // Since the order item is already in the in-memory `cart.order_items`
        // list, if we call `cart.add_order_item`, it will refuse to save
        // to the server side. So let's pop it out first.
        cart.order_items.pop();

        cart.add_order_item(orderItem, () => {
          this.forceUpdate();
        });
      }
    });
  };

  @Throttle(100)
  update(): void {
    this.props.cart.populate_with_server_data(this.forceUpdate.bind(this));
  }

  render() {
    let { cart } = this.props;
    let shipping_to =
      cart.country_code !== "NZ"
        ? to_country_name(cart.country_code)
        : cart.postal_code;
    let order_items = cart.order_items.map(order_item => (
      <OrderItemDescriptor
        key={"order_item:" + order_item.id}
        orderItem={order_item}
        hideToolbar={this.props.hasCheckout && window.role !== "admin"}
        onUpdate={this.update}
        onDelete={this.handleDelete}
      />
    ));

    let recommendations = cart.recommendations.map(option => (
      <UpsellingItemDescriptor
        key={`upselling_item:${option.product}-${option.size}`}
        orderItem={
          new OrderItem(
            option.name || "My project",
            option.product,
            option.size,
            [],
            {},
            option.quantity,
            option.subtotal
          )
        }
        onUpdate={this.addUpsellingItemToCart}
      />
    ));
    let upselling_items =
      recommendations.length > 0 ? (
        <div className={"cart--upselling-items"}>
          <h3>You may like to add</h3>
          {recommendations}
        </div>
      ) : (
        ""
      );

    let checkoutWarning = (
      <div className="col-xs-12 col-sm-5 pull-right">
        <div className="alert alert-warning">
          This cart has been checked out. Please contact
          <code>hello@happymoose.nz</code> if you want to make any changes.
        </div>
      </div>
    );

    let checkoutSummary = (
      <div className="col-xs-12 col-sm-5 pull-right">
        <div className="cart--sidebar">
          <div className={"form"}>
            <table className="table">
              <tbody>
                <tr className="subtotal">
                  <th>Subtotal</th>
                  <td>{cart.subtotal}</td>
                </tr>
                <tr className="packaging">
                  <th>Packaging</th>
                  <td>{cart.packaging_price}</td>
                </tr>
                <tr className="shipping">
                  <th>Shipping</th>
                  <td>{cart.shipping_price}</td>
                </tr>
                <tr className="total">
                  <th>Total (incl. GST)</th>
                  <td>{cart.total}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="cart--proceed-to-checkout call-to-action">
            <button
              className="btn btn-success btn-control btn-control_highlighted"
              onClick={this.handleCheckout}
              disabled={window.maintenanceMode}>
              {this.props.hasCheckout ? "Save changes" : "Proceed to checkout"}
            </button>
            {window.maintenanceMode && (
              <p className="text-warning" style={{ marginTop: "8px" }}>
                Our store is taking a little break. Ordering is paused for
                now. See you soon!
              </p>
            )}
          </div>
          {cart.country_code !== "NZ" && cart.cart_weight > 20.0 ? (
            <span className="text-info">
              Wow, looks like you are buying quite a few items. Please note that
              your order will be sent as multiple packages because of the 20 kg
              international shipping limit.
            </span>
          ) : (
            ""
          )}

          <dl className="sidebar">
            <dt>Shipping cost</dt>
            <dd>
              The GST {cart.country_code !== "NZ" ? "excl." : "incl."} price is
              based on shipping to
              {cart.country_code === "NZ" ? " post code " : " "}
              <code>{shipping_to}</code>. Sending to a different address?
              {cart.country_code === "NZ"
                ? " Input the postal code below and press enter"
                : " Select a country below"}
              <br />
              <CountrySelector
                onChange={this.handleCountryChanged}
                country_code={cart.country_code}
              />
              <input
                className={`form-control postal_code ${
                  cart.country_code !== "NZ" ? "hide" : ""
                }`}
                type="text"
                placeholder={cart.postal_code}
                onKeyDown={this.handlePostalCodeChanged}
                disabled={cart.country_code !== "NZ"}
              />
            </dd>

            <dt>Production time</dt>
            <dd>
              Ready to ship in 3-7 business days.
              <br />
              Ready to ship Want it faster? During the checkout, upgrade to
              priority order.
            </dd>
            <dt>Promo code</dt>
            <dd>
              Have a gift card or promotional code? Add it in the next step.
            </dd>
            <dt>For more information</dt>
            <dd>
              Check out the{" "}
              <a
                href="https://help.happymoose.nz/collection/7-orders-shipping"
                target="_blank">
                Shopping Cart, Checkout and Shipping FAQ
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>{" "}
              or{" "}
              <a href="/pages/contact.html" target="_blank">
                Contact Support
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
              .
            </dd>
          </dl>
        </div>
      </div>
    );

    return (
      <div>
        <div className="cart">
          <h1>
            Shopping cart
            <a href={"/products"}>&nbsp; Continue shopping</a>
          </h1>
          <div className="row">
            <SentryBoundary>
              <div className="cart--content col-xs-12 col-sm-7">
                <div className={"cart--order-items"}>{order_items}</div>
                {upselling_items}
              </div>
              {this.props.hasCheckout && window.role !== "admin"
                ? checkoutWarning
                : checkoutSummary}
            </SentryBoundary>
          </div>
        </div>
      </div>
    );
  }
}
